import { ConfirmationDialog, FormattedText } from "client/Components";
import { useDataInputDialog } from "client/Hooks";
import { localize } from "client_legacy/ndaw/culture";
import { ReactElement, useCallback } from "react";

export type DeleteConfirmationDialogProps = {
	name: string;
	content?: ReactElement;
	onConfirm(): void;
	onCancel(): void;
};

export const DeleteConfirmationDialog = (props: DeleteConfirmationDialogProps): ReactElement => {
	const {
		name,
		content,
		onConfirm,
		onCancel
	} = props;

	return (
		<ConfirmationDialog
			trackingId="delete-confirmation-dialog"
			title={(
				<FormattedText
					text={localize("_AreYouSureYouWantToDeleteName")}
					thingsToReplace={[
						{
							what: "{Name}",
							filler: name
						}
					]}
				/>
			)}
			confirmText={localize("_Delete")}
			onConfirmation={onConfirm}
			onCancellation={onCancel}
		>
			{content}
		</ConfirmationDialog>
	);
};
DeleteConfirmationDialog.displayName = "DeleteConfirmationDialog";

export type DeleteConfirmationDialogData = Omit<DeleteConfirmationDialogProps, "onConfirm" | "onCancel">;

export function useDeleteConfirmationDialog(): [
	element: ReactElement | null,
	open: (data: DeleteConfirmationDialogData) => Promise<boolean>
] {
	const [element, originalOpen] = useDataInputDialog<DeleteConfirmationDialogData, void>(
		(initialData, onCancel, onConfirm) => (
			<DeleteConfirmationDialog
				{...initialData}
				onCancel={onCancel}
				onConfirm={onConfirm}
			/>
		)
	);

	const open = useCallback(
		async (initialData: DeleteConfirmationDialogData): Promise<boolean> => {
			return (await originalOpen(initialData)).wasSuccessful;
		},
		[originalOpen]
	);

	return [
		element,
		open
	];
}